/**
 * Shared layer for remote (URL-based) MCP servers. Two flavours:
 *
 *   1. OAuth bearer (Granola, future Xero MCP): a single entry in
 *      `OAUTH_PROVIDERS` with `mcpUrl: '<endpoint>'`. The manager writes
 *      `<DEFINITIONID>_ACCESS_TOKEN` (uppercase-snake) to the agent env and
 *      this module emits an `Authorization: Bearer ${...}` header. Refresh is
 *      driven by the existing `oauth-refresh.ts` cron + manual paths.
 *
 *   2. ENG-5855: custom-header api-key (Anchor Browser): a `remoteMcp` spec
 *      on the `INTEGRATION_REGISTRY` entry carrying an arbitrary templated
 *      headers map (e.g. `anchor-api-key` + a dynamic `anchor-session-id`).
 *      No OAuth, no bearer — the header names and `${VAR}` values come
 *      straight from the spec. This is the data-driven sibling of the
 *      `nativeMcp` path (ENG-5815).
 *
 * Both flavours rely on Claude Code's spawn-time `${VAR}` substitution from
 * `.env.integrations` (same mechanism as `command: 'npx', env: { X: '${X}' }`
 * already used for stdio servers like Xero).
 *
 * Public clients (no client_secret, PKCE-only) and confidential clients are
 * both handled the same way here — the difference is at /authorize and
 * /callback, not at MCP wiring time.
 */

import { OAUTH_PROVIDERS } from '../integrations/oauth-providers.js';
import { INTEGRATION_REGISTRY } from '../integrations/registry.js';
import {
  isDefaultRemoteMcpConnection,
  remoteMcpConnectionEnvInfix,
  remoteMcpConnectionLabel,
  remoteMcpConnectionScopedEnvVar,
} from '../integrations/remote-mcp-connection.js';
import type { RemoteMcpSpec, HitlTier } from '../types/integration.js';
// ENG-10614: imported rather than re-expressed. `isAutoTier` auto-executes
// `read` AND `write`, so a local "anything above read" test would gate every
// ordinary write - the mistake users notice immediately.
import { isAutoTier } from '../integrations/hitl-resolver.js';
import { REMOTE_MCP_PROXY_ENV } from '../integrations/remote-mcp-proxy-env.js';

export interface RemoteMcpEntry {
  /**
   * Transport. Required by Claude Code's MCP schema — without it a URL-
   * based entry fails validation at claude startup ("Does not adhere to
   * MCP server configuration schema") and the agent's tmux session
   * exits inside a second. Two valid values:
   *   - 'http' → Streamable HTTP transport (newer, default for our
   *     OAuth-MCP integrations)
   *   - 'sse'  → Server-Sent Events transport (older)
   * Per Claude Code docs: every example with `url` + `headers` also has
   * `type`. ENG-5074 caught this in prod when Scout flapped for hours
   * after the ENG-5071 sanitizer fix correctly preserved `{url, headers}`
   * but didn't add a `type` field — and the writer/buildMcpJson paths
   * had never been emitting one either.
   */
  type: 'http' | 'sse';
  /** The MCP server URL (streamable-HTTP or SSE per `type`). */
  url: string;
  /**
   * Headers to send with each MCP request. Templated values like
   * `${VAR}` are substituted by the Claude Code MCP launcher at spawn time
   * from the spawn env (same mechanism as `command: 'npx', env: { X: '${X}' }`
   * already used for stdio servers like Xero).
   */
  headers?: Record<string, string>;
}

// ENG-5855: `RemoteMcpSpec` (the declarative registry-side contract) is a
// shared domain type — it lives in `../types/integration.ts` alongside the
// other integration types and is re-exported here for ergonomic access from
// the provisioning layer.
export type { RemoteMcpSpec };

/**
 * Convert a definition_id (kebab-case) into the access-token env var name.
 *
 * ENG-8359: a NAMED connection carries its own infix (`BRAND_NINJA__SECONDARY_
 * ACCESS_TOKEN`) so two connections of one definition don't share a credential.
 * The default connection is unchanged, so every existing agent's env — and the
 * `<PREFIX>_ACCESS_TOKEN` convention documented to agents — is untouched.
 */
export function envVarForToken(definitionId: string, connectionKey?: string | null): string {
  const idPart = definitionId.replace(/-/g, '_').toUpperCase();
  return `${idPart}${remoteMcpConnectionEnvInfix(connectionKey)}_ACCESS_TOKEN`;
}

/**
 * ENG-6993 / ADR-0033 (C1): derive the env var for a structured-`auth`
 * credential from the integration's OWN `definition_id` + the credential field
 * name — `<DEFINITION_ID>_<CREDENTIAL_REF>` (e.g. `anchor-browser` + `api_key`
 * → `ANCHOR_BROWSER_API_KEY`; `monday` + `api_key` → `MONDAY_API_KEY`). Because
 * the name is derived from the integration's own id, a `RemoteMcpAuth` can
 * never reference a different integration's secret — there is no free-form
 * `${VAR}` for an operator/catalog to inject a cross-credential reference into.
 *
 * ENG-8359: a NAMED connection carries its own infix
 * (`ANCHOR_BROWSER__SECOND_API_KEY`), for the same reason as
 * {@link envVarForToken} — without it two connections' servers read one secret.
 * Default connection unchanged.
 */
export function credentialEnvVar(
  definitionId: string,
  credentialRef: string,
  connectionKey?: string | null,
): string {
  const idPart = definitionId.replace(/-/g, '_').toUpperCase();
  const credPart = credentialRef.replace(/-/g, '_').toUpperCase();
  return `${idPart}${remoteMcpConnectionEnvInfix(connectionKey)}_${credPart}`;
}

/**
 * ENG-6993 / ADR-0033 (C1, SSRF guard): a hosted-MCP `url` is written verbatim
 * into an agent's `.mcp.json`, so it must be a public HTTPS endpoint — never an
 * internal / link-local / cloud-metadata host that would turn the provisioner
 * into an SSRF relay. Throws on a disallowed url. (Catalog-driven specs should
 * also validate at row-write time; this is the belt at render time.)
 */
export function assertSafeRemoteMcpUrl(url: string, definitionId: string): void {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    throw new Error(`remoteMcp.url for '${definitionId}' is not a valid URL: ${url}`);
  }
  if (u.protocol !== 'https:') {
    throw new Error(`remoteMcp.url for '${definitionId}' must be https (got ${u.protocol}//): ${url}`);
  }
  const host = u.hostname.toLowerCase();
  const blocked =
    host === 'localhost' ||
    host === '169.254.169.254' || // AWS/GCP/Azure instance metadata
    host === 'metadata.google.internal' ||
    /^127\./.test(host) ||
    /^10\./.test(host) ||
    /^192\.168\./.test(host) ||
    /^169\.254\./.test(host) || // link-local
    /^172\.(1[6-9]|2\d|3[01])\./.test(host) || // 172.16.0.0/12
    host.endsWith('.internal') ||
    host.endsWith('.local');
  if (blocked) {
    throw new Error(`remoteMcp.url for '${definitionId}' resolves to a disallowed (internal/link-local/metadata) host: ${host}`);
  }
}

/**
 * ENG-6993 / ADR-0033: render a `RemoteMcpSpec` into a `.mcp.json` entry. The
 * structured `auth` field (preferred) is turned into a credential header whose
 * env var is DERIVED from `definitionId` + `auth.credential_ref` (so it is
 * scoped to this integration — C1); any legacy `headers` (non-secret / dynamic,
 * e.g. Anchor's `anchor-session-id`) are merged on top. `auth` wins if a header
 * of the same name is also present in `headers`.
 */
export function renderRemoteMcpSpec(
  definitionId: string,
  spec: RemoteMcpSpec,
  connectionKey?: string | null,
): RemoteMcpEntry {
  assertSafeRemoteMcpUrl(spec.url, definitionId);

  // Auth header first, then the legacy/dynamic headers — preserves the key
  // order of pre-migration specs (e.g. Anchor: api-key header, then
  // session-id) so the rendered entry is byte-identical across the migration.
  const headers: Record<string, string> = {};

  if (spec.auth) {
    const envVar = credentialEnvVar(definitionId, spec.auth.credential_ref, connectionKey);
    const value = `\${${envVar}}`;
    if (spec.auth.scheme === 'bearer') {
      headers['Authorization'] = `Bearer ${value}`;
    } else {
      if (!spec.auth.header_name) {
        throw new Error(`remoteMcp.auth for '${definitionId}' uses scheme 'header' but no header_name`);
      }
      headers[spec.auth.header_name] = value;
    }
  }

  // ENG-8359: a spec header may reference one of the integration's OWN
  // config-derived vars (Anchor's `${ANCHOR_BROWSER_SESSION_ID}`). Those are
  // published per connection, so the reference has to follow — otherwise the
  // second connection's server authenticates with its own credential but reads
  // the FIRST connection's session id. Identity for the default connection, so
  // every existing entry renders byte-identically.
  // The default connection takes the original verbatim assign, so the rendered
  // entry stays byte-identical (no re-normalisation of a `${VAR}` value).
  if (isDefaultRemoteMcpConnection(connectionKey)) {
    Object.assign(headers, spec.headers ?? {});
  } else {
    for (const [header, value] of Object.entries(spec.headers ?? {})) {
      const varName = extractHeaderVarName(value);
      headers[header] = varName
        ? `\${${remoteMcpConnectionScopedEnvVar(definitionId, varName, connectionKey)}}`
        : value;
    }
  }

  return {
    type: spec.type ?? 'http',
    url: spec.url,
    ...(Object.keys(headers).length > 0 ? { headers } : {}),
  };
}

/**
 * Build the `.mcp.json` entry for a remote MCP integration, based on its
 * OAuth provider config. Returns null when the definition_id has no
 * `mcpUrl` registered (i.e. it's a stdio MCP, a Composio proxy, or not
 * an MCP at all).
 *
 * Whenever `mcpUrl` is present, the entry includes an
 * `Authorization: Bearer ${<DEFINITIONID>_ACCESS_TOKEN}` header. Manager
 * substitutes the env var at MCP-spawn time from the agent's resolved
 * `credentials.access_token` (refreshed via `oauth-refresh.ts`).
 *
 * Integrations whose OAuth is brokered by the MCP host itself (no token
 * flows through our infrastructure — e.g. Granola pre-ENG-4693) should
 * use {@link buildHostBrokeredRemoteMcpEntry} at the call site instead;
 * adding such providers to OAUTH_PROVIDERS without a real OAuth wiring
 * would cause this helper to inject an unresolvable `${...}` placeholder.
 */
export function buildRemoteMcpEntry(
  definitionId: string,
  dbSpec?: RemoteMcpSpec | null,
  connectionKey?: string | null,
): RemoteMcpEntry | null {
  // ENG-6993 / ADR-0033 (Slice 2): the DB catalog row wins. When the caller
  // forwards the integration's `integration_definitions.remote_mcp` column
  // (carried on `ResolvedIntegration.remoteMcp`), render from that — the
  // catalog is the source of truth. The code `INTEGRATION_REGISTRY` remains a
  // fallback/seed for callers not yet wired to forward the column and for the
  // test suite, so unmigrated paths stay byte-identical. This is what retires
  // the code/DB duality: a brand-new remote-MCP integration (e.g. monday.com)
  // can ship as a pure catalog row with no code-registry entry at all.
  //
  // ENG-5855 origin: data-driven custom-header path takes precedence over the
  // OAuth bearer path — declared headers (api-key auth, dynamic session header)
  // emit verbatim, mirroring the `nativeMcp` data-driven path.
  const spec = dbSpec ?? INTEGRATION_REGISTRY.find((d) => d.id === definitionId)?.remoteMcp;
  if (spec) {
    // Render via the shared descriptor renderer — handles the structured `auth`
    // field (scoped, derived env var) + any legacy/dynamic `headers`, and
    // validates the url. Backward-compatible for specs that carry only `headers`.
    return renderRemoteMcpSpec(definitionId, spec, connectionKey);
  }

  const provider = OAUTH_PROVIDERS[definitionId];
  if (!provider?.mcpUrl) return null;

  // OAuth-wired remote MCP: include the bearer header. Manager substitutes
  // the env var from the agent's refreshed access_token at spawn time.
  return {
    type: 'http',
    url: provider.mcpUrl,
    headers: {
      Authorization: `Bearer \${${envVarForToken(definitionId, connectionKey)}}`,
    },
  };
}

/**
 * Variant for integrations that have a known MCP URL but no OAuth wiring in
 * `OAUTH_PROVIDERS` yet — used as an escape hatch while end-user OAuth is
 * being built (e.g. Granola pre-ENG-4693, where Claude Code itself brokers
 * the auth on the host).
 */
export function buildHostBrokeredRemoteMcpEntry(url: string): RemoteMcpEntry {
  return { type: 'http', url };
}

/** A `.mcp.json` stdio server entry (command + args + env). */
export interface StdioMcpEntry {
  command: string;
  args: string[];
  env: Record<string, string>;
}

export interface RemoteOAuthProxyPaths {
  /** Absolute path to the bundled proxy on the host (`~/.augmented/_mcp/remote-oauth-proxy.js`). */
  proxyPath: string;
  /** Absolute path to the per-agent secrets file the manager keeps fresh (`<projectDir>/.env.integrations`). */
  tokenFile: string;
}

/**
 * ENG-6859: build the stdio-proxy `.mcp.json` entry for an OAuth-wired remote
 * MCP integration (one with `OAUTH_PROVIDERS[id].mcpUrl`), replacing the old
 * direct streamable-HTTP entry whose `Bearer ${<ID>_ACCESS_TOKEN}` header was
 * frozen at session spawn.
 *
 * The entry launches `node <proxyPath>`, which speaks MCP stdio to Claude Code
 * and forwards to the remote URL using the CURRENT token read from `tokenFile`
 * on EVERY request - so a token rotated mid-session is picked up without a
 * restart. The URL / file path / var name are baked as literals (they are
 * stable); only the token they point at rotates, and that is read live by the
 * proxy rather than substituted by Claude Code at spawn.
 *
 * Returns null when the integration is not an OAuth-wired remote MCP (no
 * provider, no `mcpUrl`, or it uses the data-driven custom-header `remoteMcp`
 * path - those carry non-rotating api-keys and stay on the direct HTTP entry
 * from {@link buildRemoteMcpEntry}).
 */
/**
 * ENG-7748: extract the env var name from a single `${VAR}` header template.
 * Returns null for any value that isn't exactly one clean placeholder (a literal
 * header value can't be "live-read" and is skipped by the proxy-entry builder).
 */
export function extractHeaderVarName(value: string): string | null {
  const m = /^\$\{([A-Za-z_][A-Za-z0-9_]*)\}$/.exec(value.trim());
  return m ? m[1]! : null;
}

/**
 * ENG-7748: build the stdio-proxy `.mcp.json` entry for a remote MCP whose spec
 * opts into live header refresh (`liveHeaderRefresh: true`, i.e. Anchor). Unlike
 * the direct streamable-HTTP entry from {@link buildRemoteMcpEntry} - whose
 * headers Claude Code freezes at spawn - this routes through the same stdio proxy
 * as the OAuth remotes, which reads every header value LIVE from the token file
 * per request. So a re-minted `anchor-session-id` takes effect with no respawn.
 *
 * The proxy is configured from the spec:
 *   - the `auth` credential (header scheme) → forwarded as `auth.header_name`
 *     (e.g. `anchor-api-key`), value read live from the derived credential env var;
 *   - each `${VAR}` entry in `headers` → forwarded as a live-read header
 *     (e.g. `anchor-session-id` ← `ANCHOR_BROWSER_SESSION_ID`).
 *
 * Returns null when the spec is absent or does not opt in - those keep the
 * direct-HTTP entry. Throws only on a misconfigured opt-in (no header-scheme
 * `auth`), which is a catalog/registry authoring error, not a runtime condition.
 */
/**
 * ENG-10614: the per-tool HITL floors for one remote MCP, straight from
 * `tool_definitions.min_hitl_tier` keyed on `(toolkit_id, tool_key)`.
 *
 * Carried as a plain record rather than a Map because it crosses the API ->
 * manager -> provisioning boundary as JSON.
 */
export type RemoteMcpToolTiers = Readonly<Record<string, HitlTier>>;

/**
 * Does this catalog contain anything that must not auto-execute?
 *
 * `isAutoTier` is imported rather than re-expressed: `read` AND `write` both
 * auto-execute, so the intuitive "anything above read" test would gate every
 * ordinary write and is the single easiest way to get this wrong in the
 * direction users notice.
 */
export function hasGatedTool(tiers: RemoteMcpToolTiers | null | undefined): boolean {
  if (!tiers) return false;
  // ENG-10614 / CodeRabbit: a PRESENT BUT EMPTY table gates. It does not mean
  // "nothing to enforce" - it means the catalog had rows and none of them
  // resolved to a tier this binary knows, so EVERY tool is unknown and the
  // proxy must refuse the lot. Only an ABSENT table (undefined/null) means
  // "no catalog here, forward as before".
  //
  // Returning false here was the second half of the same hole as
  // `normalizeToolTiers`: fixing that to return `{}` achieves nothing if this
  // reads `{}` as "no gated tool" and leaves the integration on a direct entry.
  if (Object.keys(tiers).length === 0) return true;
  return Object.values(tiers).some((tier) => !isAutoTier(tier));
}

/**
 * The `AGT_REMOTE_MCP_TOOL_TIERS` env fragment, or `{}` when there is nothing
 * to say.
 *
 * Emitting NOTHING rather than an empty table when the catalog is empty is
 * deliberate and is the counterpart of the proxy's three-state parse: an empty
 * table there means "authoritative, and every tool is unknown", which would
 * refuse every call on an integration we simply have no rows for. Absent means
 * "no catalog", which forwards.
 */
export function toolTiersEnv(tiers: RemoteMcpToolTiers | null | undefined): Record<string, string> {
  // ABSENT only. An empty-but-present table is emitted as `{}` on purpose: the
  // proxy parses that into its authoritative `table` state with zero known
  // tools, so every `tools/call` misses the lookup and is refused as unknown.
  // That is the fail-closed answer for "the catalog said something and we could
  // not read it", and it is why this no longer short-circuits on length 0.
  if (!tiers) return {};
  return { [REMOTE_MCP_PROXY_ENV.toolTiers]: JSON.stringify(tiers) };
}

export function buildLiveHeaderRemoteMcpProxyEntry(
  definitionId: string,
  dbSpec: RemoteMcpSpec | null | undefined,
  paths: RemoteOAuthProxyPaths,
  connectionKey?: string | null,
  toolTiers?: RemoteMcpToolTiers | null,
): StdioMcpEntry | null {
  const spec = dbSpec ?? INTEGRATION_REGISTRY.find((d) => d.id === definitionId)?.remoteMcp;
  if (!spec?.liveHeaderRefresh) return null;

  assertSafeRemoteMcpUrl(spec.url, definitionId);

  if (!spec.auth || spec.auth.scheme !== 'header' || !spec.auth.header_name) {
    throw new Error(
      `remoteMcp.liveHeaderRefresh for '${definitionId}' requires a header-scheme 'auth' with a header_name`,
    );
  }

  // Extra headers: each `${VAR}` entry in `headers` becomes a live-read header.
  // Literal-valued headers can't be live and are skipped (Anchor has none).
  const extraPairs: string[] = [];
  for (const [header, value] of Object.entries(spec.headers ?? {})) {
    const varName = extractHeaderVarName(value);
    // ENG-8359: point the live-read var at THIS connection's copy, for the same
    // reason as renderRemoteMcpSpec — otherwise both connections' proxies read
    // one session id. Identity for the default connection.
    if (varName) {
      extraPairs.push(`${header}:${remoteMcpConnectionScopedEnvVar(definitionId, varName, connectionKey)}`);
    }
  }

  return {
    command: 'node',
    args: [paths.proxyPath],
    env: {
      AGT_REMOTE_MCP_URL: spec.url,
      AGT_REMOTE_MCP_TOKEN_FILE: paths.tokenFile,
      AGT_REMOTE_MCP_TOKEN_VAR: credentialEnvVar(definitionId, spec.auth.credential_ref, connectionKey),
      AGT_REMOTE_MCP_AUTH_HEADER: spec.auth.header_name,
      AGT_REMOTE_MCP_LABEL: remoteMcpConnectionLabel(definitionId, connectionKey),
      ...(extraPairs.length > 0 ? { AGT_REMOTE_MCP_EXTRA_HEADERS: extraPairs.join(',') } : {}),
      // ENG-10614: the catalogued HITL floors. anchor-browser reaches the proxy
      // via this branch, so this is where its one gated tool
      // (`anchor_network_requests`, write_high_risk) starts being enforced.
      ...toolTiersEnv(toolTiers),
    },
  };
}

export function buildOAuthRemoteMcpProxyEntry(
  definitionId: string,
  paths: RemoteOAuthProxyPaths,
  connectionKey?: string | null,
  toolTiers?: RemoteMcpToolTiers | null,
): StdioMcpEntry | null {
  // Custom-header integrations (api-key, e.g. anchor-browser) keep the direct
  // HTTP entry - their credential doesn't rotate on the session's timescale.
  const def = INTEGRATION_REGISTRY.find((d) => d.id === definitionId);
  if (def?.remoteMcp) return null;

  const provider = OAUTH_PROVIDERS[definitionId];
  if (!provider?.mcpUrl) return null;

  return {
    command: 'node',
    args: [paths.proxyPath],
    env: {
      AGT_REMOTE_MCP_URL: provider.mcpUrl,
      AGT_REMOTE_MCP_TOKEN_FILE: paths.tokenFile,
      AGT_REMOTE_MCP_TOKEN_VAR: envVarForToken(definitionId, connectionKey),
      AGT_REMOTE_MCP_LABEL: remoteMcpConnectionLabel(definitionId, connectionKey),
      // ENG-6948: cap the agent's exposed surface to the curated allowlist. The
      // proxy filters tools/list and gates tools/call against this set. Omitted
      // when the provider has no allowlist, leaving the proxy a pass-through.
      ...(provider.toolAllowlist && provider.toolAllowlist.length > 0
        ? { AGT_REMOTE_MCP_TOOL_ALLOWLIST: provider.toolAllowlist.join(',') }
        : {}),
      // CS-1446: toolsets to pre-activate at session start so gated tools are
      // advertised in the connect-time tools/list the harness freezes. Omitted
      // when the provider configures none (no pre-enable, the default).
      // ENG-8512: argument rules for calls the remote would accept and silently
      // ignore. JSON rather than a delimited list because a rule carries a regex
      // and a human-readable message, and both can contain any separator we
      // might have picked. Omitted when the provider declares none, leaving the
      // proxy's argument checking entirely off.
      ...(provider.argRejects && provider.argRejects.length > 0
        ? { AGT_REMOTE_MCP_ARG_REJECTS: JSON.stringify(provider.argRejects) }
        : {}),
      ...(provider.preEnableToolsets && provider.preEnableToolsets.length > 0
        ? { AGT_REMOTE_MCP_PREENABLE_TOOLSETS: provider.preEnableToolsets.join(',') }
        : {}),
      // ENG-10614: catalogued HITL floors, orthogonal to the allowlist above.
      // The allowlist answers "may this agent see the tool at all"; this answers
      // "may it run without a human". A tool can be allowlisted and still gated.
      ...toolTiersEnv(toolTiers),
    },
  };
}

/**
 * ENG-10614: proxy entry for an API-KEY hosted remote MCP (monday, peec) that
 * carries at least one gated tool.
 *
 * WHY THIS BRANCH HAS TO EXIST. Before this, an api-key remote MCP with no
 * `liveHeaderRefresh` and no OAuth provider fell through to
 * {@link buildRemoteMcpEntry} - a DIRECT streamable-HTTP `.mcp.json` entry.
 * Claude Code then called the vendor with no Augmented code anywhere in the
 * request path, so there was no place a tier check could run. The gate in
 * `remote-oauth-proxy.ts` is unreachable for monday until its traffic goes
 * through the proxy, and a gate nothing reaches is decoration.
 *
 * Returns null when there is nothing to gate, so an integration with an
 * all-`read` catalog (peec today) keeps its direct entry and its current
 * latency. Routing traffic through a proxy is not free and is only justified by
 * something to enforce.
 *
 * Deliberately AFTER the live-header and OAuth branches at the call site: those
 * carry extra per-request behaviour (live headers, token rotation) that this
 * one does not, so an integration that qualifies for either must keep it.
 */
export function buildApiKeyRemoteMcpProxyEntry(
  definitionId: string,
  dbSpec: RemoteMcpSpec | null | undefined,
  paths: RemoteOAuthProxyPaths,
  connectionKey?: string | null,
  toolTiers?: RemoteMcpToolTiers | null,
): StdioMcpEntry | null {
  if (!hasGatedTool(toolTiers)) return null;

  const spec = dbSpec ?? INTEGRATION_REGISTRY.find((d) => d.id === definitionId)?.remoteMcp;
  if (!spec) return null;
  // A live-header spec is handled by its own builder, which does more than this
  // one. Guarding here as well keeps the two branches independently correct
  // rather than relying on call order alone.
  if (spec.liveHeaderRefresh) return null;
  if (!spec.auth?.credential_ref) return null;

  assertSafeRemoteMcpUrl(spec.url, definitionId);

  const tokenVar = credentialEnvVar(definitionId, spec.auth.credential_ref, connectionKey);
  // `bearer` is the default and needs no header name; a `header` scheme names
  // its own. Anything else is not something this builder can render, and
  // returning null leaves the direct entry rather than emitting a proxy that
  // would authenticate wrongly.
  const scheme = spec.auth.scheme ?? 'bearer';
  if (scheme !== 'bearer' && scheme !== 'header') return null;
  if (scheme === 'header' && !spec.auth.header_name) return null;

  // ENG-10614 / CodeRabbit: CARRY `spec.headers` ACROSS THE DIVERSION.
  //
  // Diverting a spec from a direct entry to this proxy silently dropped any
  // extra headers it declared, so an integration that worked yesterday could
  // start failing the moment a tool-tier change routed it through here. The
  // trigger would be a catalog edit, and the symptom an auth failure in a
  // completely different place - about as hard to trace as it gets.
  //
  // No shipped spec hits this today (anchor-browser is the only one with
  // headers and it has its own live-header builder), so this is a trap being
  // closed before it is sprung rather than a live break.
  const extraPairs: string[] = [];
  for (const [header, value] of Object.entries(spec.headers ?? {})) {
    const varName = extractHeaderVarName(value);
    if (!varName) {
      // A literal header value cannot be expressed through the proxy, which
      // reads every extra header live from the secrets file by var name.
      //
      // THROW rather than take either quiet option. Dropping the header breaks
      // the integration invisibly; returning null falls back to the DIRECT
      // entry, which silently removes the tier gate this whole change exists to
      // install - and a security control that disappears because of an
      // unrelated header is the worst of the three. Failing at provision time
      // with a named reason is the only option that cannot be missed, and it
      // matches how buildLiveHeaderRemoteMcpProxyEntry already refuses a spec
      // it cannot render.
      throw new Error(
        `remoteMcp for '${definitionId}' declares a literal header '${header}' and also has a gated tool, ` +
          `which the tier-gating proxy cannot render (extra headers must be '\${VAR}' references). ` +
          `Give the header a '\${VAR}' value, or the integration would have to run un-gated.`,
      );
    }
    extraPairs.push(`${header}:${remoteMcpConnectionScopedEnvVar(definitionId, varName, connectionKey)}`);
  }

  return {
    command: 'node',
    args: [paths.proxyPath],
    env: {
      AGT_REMOTE_MCP_URL: spec.url,
      AGT_REMOTE_MCP_TOKEN_FILE: paths.tokenFile,
      AGT_REMOTE_MCP_TOKEN_VAR: tokenVar,
      AGT_REMOTE_MCP_LABEL: remoteMcpConnectionLabel(definitionId, connectionKey),
      ...(scheme === 'header' && spec.auth.header_name
        ? { AGT_REMOTE_MCP_AUTH_HEADER: spec.auth.header_name }
        : {}),
      ...(extraPairs.length > 0 ? { AGT_REMOTE_MCP_EXTRA_HEADERS: extraPairs.join(',') } : {}),
      ...toolTiersEnv(toolTiers),
    },
  };
}
